// UniFi Access API Integration Module for Client Portal
// Handles visitor creation via the proxy server

const PROXY_URL = "/api";

/**
 * Make an API request to UniFi Access via the proxy
 * @param {string} method - HTTP method (GET, POST, etc.)
 * @param {string} endpoint - API endpoint (without /api prefix)
 * @param {object} body - Request body for POST/PUT requests
 * @param {string} token - Authorization token
 * @returns {Promise<object>} The response data
 */
async function makeUniFiRequest(method, endpoint, body = null, token = "") {
    try {
        console.log(`Making UniFi API request: ${method} ${endpoint}`);
        console.log(`Token provided: ${token ? "Yes" : "No"}`);
        console.log(`Body: ${body ? JSON.stringify(body) : "None"}`);

        const url = `${PROXY_URL}${endpoint}`;
        console.log(`Full URL: ${url}`);

        const options = {
            method,
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
        };

        if (token) {
            options.headers.Authorization = `Bearer ${token}`;
            console.log(
                `Authorization header set: Bearer ${token.substring(0, 10)}...`,
            );
        } else {
            console.warn("No token provided for UniFi request!");
        }

        if (body && ["POST", "PUT", "DELETE"].includes(method)) {
            options.body = JSON.stringify(body);
        }

        const response = await fetch(url, options);
        console.log(`Response status: ${response.status}`);
        console.log(`Response headers:`, response.headers);

        const text = await response.text();
        console.log(`Response text: ${text.substring(0, 500)}`);

        if (!response.ok) {
            console.error(`HTTP Error ${response.status}: ${text}`);
            throw new Error(`HTTP ${response.status}: ${text}`);
        }

        if (!text) {
            throw new Error("Empty response from UniFi API");
        }

        const data = JSON.parse(text);
        console.log(`Parsed response:`, data);
        return data;
    } catch (error) {
        console.error("UniFi API Request Error:", error);
        throw error;
    }
}

/**
 * Create a new visitor in UniFi Access
 * @param {string} firstName - First name
 * @param {string} lastName - Last name (surname)
 * @param {string} email - Email address
 * @param {string} phoneNumber - Phone number (optional)
 * @param {string} token - UniFi Access API token
 * @returns {Promise<object>} Created visitor object
 */
async function createUniFiVisitor(
    firstName,
    lastName,
    email,
    phoneNumber = "",
    token = "",
) {
    try {
        // Create visitor with today's date range (00:00 to 23:59)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const startTime = Math.floor(today.getTime() / 1000);

        const endTime = new Date(today);
        endTime.setHours(23, 59, 59, 999);
        const endTimeUnix = Math.floor(endTime.getTime() / 1000);

        const visitorData = {
            first_name: firstName,
            last_name: lastName,
            email: email,
            start_time: startTime,
            end_time: endTimeUnix,
        };

        // Add phone if provided
        if (phoneNumber) {
            visitorData.mobile_phone = phoneNumber;
        }

        console.log("Creating UniFi visitor with payload:", visitorData);

        const response = await makeUniFiRequest(
            "POST",
            "/visitors",
            visitorData,
            token,
        );

        console.log("UniFi visitor created:", response);

        // UniFi returns the created visitor in the response
        if (response && response.data) {
            return response.data;
        }

        return response;
    } catch (error) {
        console.error("Error creating UniFi visitor:", error);
        throw error;
    }
}

export { makeUniFiRequest, createUniFiVisitor };
